import { useState, useEffect, useCallback } from 'react';

interface TimerState {
  isRunning: boolean;
  time: number;
  startTime: number | null;
  endTime: number | null;
}

interface UseTimerOptions {
  persistKey?: string; // ключ для localStorage
  onComplete?: () => void;
}

export const useTimer = (options: UseTimerOptions = {}) => {
  const {
    persistKey = 'timer',
    onComplete
  } = options;

  // Загружаем состояние из localStorage при инициализации
  const loadPersistedState = (): TimerState => {
    try {
      const saved = localStorage.getItem(persistKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Проверяем, что у нас есть правильные данные
        if (parsed.time && typeof parsed.time === 'number' && parsed.time > 0) {
          // Если таймер был запущен, восстанавливаем его состояние
          if (parsed.isRunning && parsed.startTime && parsed.endTime) {
            const now = Date.now();
            const totalDuration = parsed.endTime - parsed.startTime;
            const elapsed = now - parsed.startTime;
            const remaining = Math.max(0, totalDuration - elapsed);
            
            return {
              isRunning: remaining > 0,
              time: Math.floor(remaining / 1000),
              startTime: remaining > 0 ? parsed.startTime : null,
              endTime: remaining > 0 ? parsed.endTime : null
            };
          }
          
          // Если таймер был остановлен, просто восстанавливаем время
          return {
            isRunning: false,
            time: parsed.time,
            startTime: null,
            endTime: null
          };
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки таймера из localStorage:', error);
    }
    
    return {
      isRunning: false,
      time: 0,
      startTime: null,
      endTime: null
    };
  };

  const [timerState, setTimerState] = useState<TimerState>(loadPersistedState);

  // Сохраняем состояние в localStorage
  const savePersistedState = useCallback((state: TimerState) => {
    try {
      // Проверяем, что время разумное (не больше 24 часов)
      const timeToSave = state.time > 0 && state.time <= 86400 ? state.time : 0;
      
      // Сохраняем полное состояние для восстановления
      localStorage.setItem(persistKey, JSON.stringify({
        time: timeToSave,
        isRunning: state.isRunning,
        startTime: state.startTime,
        endTime: state.endTime
      }));
    } catch (error) {
      console.error('Ошибка сохранения таймера в localStorage:', error);
    }
  }, [persistKey]);

  // Обновляем состояние и сохраняем в localStorage
  const updateTimerState = useCallback((updates: Partial<TimerState>) => {
    setTimerState(prev => {
      const newState = { ...prev, ...updates };
      savePersistedState(newState);
      return newState;
    });
  }, [savePersistedState]);

  // Запуск таймера - автоматически очищает старый и запускает новый
  const start = useCallback((seconds: number) => {
    if (seconds <= 0) return;
    
    // Очищаем старый таймер
    updateTimerState({
      isRunning: false,
      time: 0,
      startTime: null,
      endTime: null
    });
    
    // Запускаем новый
    const startTime = Date.now();
    const endTime = startTime + (seconds * 1000);
    
    updateTimerState({
      isRunning: true,
      time: seconds,
      startTime,
      endTime
    });
  }, [updateTimerState]);

  // Остановка таймера
  const stop = useCallback(() => {
    updateTimerState({
      isRunning: false,
      startTime: null,
      endTime: null
    });
  }, [updateTimerState]);

  // Основной эффект для обновления таймера
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (timerState.isRunning && timerState.startTime && timerState.endTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const totalDuration = timerState.endTime! - timerState.startTime!;
        const elapsed = now - timerState.startTime!;
        const remaining = Math.max(0, totalDuration - elapsed);

        if (remaining <= 0) {
          // Таймер завершен - автоматически очищаем хранилище
          updateTimerState({
            isRunning: false,
            time: 0,
            startTime: null,
            endTime: null
          });
          localStorage.removeItem(persistKey);
          onComplete?.();
        } else {
          updateTimerState({ time: Math.floor(remaining / 1000) });
        }
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timerState.isRunning, timerState.startTime, timerState.endTime, updateTimerState, onComplete, persistKey]);

  return {
    time: timerState.time,           // время в секундах
    isRunning: timerState.isRunning, // запущен ли таймер
    start,                          // start(seconds) - запустить таймер
    stop                            // stop() - остановить таймер
  };
}; 
